// ============================================================
// 📁 src/services/usersApi.ts   (فایل جدید)
// کلاینت API مدیریت کاربران + ابزارهای تاریخ شمسی/خروجی CSV
// ============================================================

declare const process: { env: { REACT_APP_API_URL?: string } };

export const API_BASE = process.env.REACT_APP_API_URL || "https://www.hamidigroupfinance.com/api";

const getToken = () => localStorage.getItem("auth_token");

async function post(endpoint: string, data: object, auth = true): Promise<any> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (auth) headers["Authorization"] = `Bearer ${getToken()}`;
  const res = await fetch(`${API_BASE}/${endpoint}`, { method: "POST", headers, body: JSON.stringify(data) });
  try {
    return await res.json();
  } catch {
    return { success: false, message: "پاسخ نامعتبر از سرور" };
  }
}
const u = (action: string, data: object = {}) => post("users.php", { action, ...data });

/* ---------------------------- انواع داده ---------------------------- */
export type PermKey = "view" | "add" | "edit" | "delete" | "approve";
export type Perm = Record<PermKey, boolean>;
export type PermMap = Record<string, Perm>;
export type UserState = "active" | "inactive" | "suspended" | "invited" | "invite_expired" | "deleted";

export interface UserType {
  id: number;
  full_name: string;
  username: string;
  email: string;
  phone: string;
  role: "admin" | "manager" | "user";
  status: "active" | "inactive";
  state: UserState;
  locked: boolean;
  avatar: string | null;
  last_login: string | null;
  join_date: string;
  job_title: string | null;
  department: string | null;
  language: string | null;
  timezone: string | null;
  totp_enabled: boolean;
  must_change_password: boolean;
  password_changed_at: string | null;
  approval_limit: number | null;
  ip_whitelist?: string | null;
  work_hours?: { from: string; to: string; days: number[] } | null;
  data_scope?: { warehouses: number[] } | null;
  permissions: PermMap;
  operations: Record<string, boolean>;
  active_grants: number;
  sessions_count: number | null;
  last_seen: string | null;
  invited_by_name: string | null;
  invite: null | { id: number; expires_at: string; last_sent_at: string | null; send_count: number; accepted_at: string | null; revoked_at: string | null; expired: boolean };
}

/* ------------------------------ API ------------------------------ */
export const uapi = {
  me: () => u("me"),
  list: (includeDeleted = false) => u("list", { include_deleted: includeDeleted }),
  dashboard: () => u("dashboard"),
  invite: (d: object) => u("invite", d),
  resendInvite: (id: number) => u("resend_invite", { id }),
  revokeInvite: (id: number) => u("revoke_invite", { id }),
  update: (d: object) => u("update", d),
  updateAvatar: (id: number, avatar: string | null) => u("update_avatar", { id, avatar }),
  updatePermissions: (id: number, permissions: object, operations?: object) => u("update_permissions", { id, permissions, operations }),
  copyPermissions: (fromId: number, toId: number) => u("copy_permissions", { fromId, toId }),
  effective: (id: number) => u("effective", { id }),
  compare: (idA: number, idB: number) => u("compare", { idA, idB }),
  grantAdd: (d: object) => u("grant_add", d),
  grantRevoke: (id: number) => u("grant_revoke", { id }),
  setState: (id: number, state: string, reason = "") => u("set_state", { id, state, reason }),
  unlock: (id: number) => u("unlock", { id }),
  remove: (id: number, confirmPassword: string, transferTo?: number) => u("delete", { id, confirmPassword, transferTo }),
  restore: (id: number) => u("restore", { id }),
  transfer: (fromId: number, toId: number, dryRun: boolean) => u("transfer_ownership", { fromId, toId, dryRun }),
  bulk: (ids: number[], op: string, extra: object = {}) => u("bulk", { ids, op, ...extra }),
  templates: () => u("templates_list"),
  templateSave: (d: object) => u("template_save", d),
  templateDelete: (id: number) => u("template_delete", { id }),
  sessions: (userId?: number) => u("sessions_list", { userId }),
  sessionRevoke: (sessionId: number) => u("session_revoke", { sessionId }),
  sessionsRevokeAll: (userId?: number) => u("sessions_revoke_all", { userId }),
  profileUpdate: (d: object) => u("profile_update", d),
  changePassword: (currentPassword: string, newPassword: string) => u("change_password", { currentPassword, newPassword }),
  sendReset: (id: number) => u("send_reset", { id }),
  forcePasswordChange: (id: number) => u("force_password_change", { id }),
  twofaStart: () => u("2fa_setup_start"),
  twofaEnable: (code: string) => u("2fa_enable", { code }),
  twofaDisable: (currentPassword: string, code: string) => u("2fa_disable", { currentPassword, code }),
  twofaBackupRegen: (currentPassword: string, code: string) => u("2fa_backup_regen", { currentPassword, code }),
  twofaReset: (id: number, confirmPassword: string) => u("2fa_reset", { id, confirmPassword }),
  settingsGet: () => u("settings_get"),
  settingsSave: (settings: object, confirmPassword?: string) => u("settings_save", { settings, confirmPassword }),
  emailTest: () => u("email_test"),
  auditList: (f: object) => u("audit_list", f),
  auditExport: (f: object) => u("audit_export", f),
  auditMeta: () => u("audit_meta"),
  loginAttempts: (f: object) => u("login_attempts_list", f),
  accessReview: () => u("access_review"),
  reviewAttest: (note: string) => u("review_attest", { note }),
  approvalCreate: (d: object) => u("approval_create", d),
  approvals: (scope: string, status = "") => u("approvals_list", { scope, status }),
  approvalDecide: (id: number, decision: "approved" | "rejected", note = "") => u("approval_decide", { id, decision, note }),
  approvalCancel: (id: number) => u("approval_cancel", { id }),
  warehouses: () => post("warehouse.php", { action: "warehouses_list" }),
};

/** ورود (با کد 2FA) و مراحل بلیت‌دار؛ توکن ذخیره نمی‌شود، توسط صفحه ورود مدیریت می‌شود */
export const authExt = {
  login: (username: string, password: string, remember: boolean, otp?: string) => post("auth.php", { action: "login", username, password, remember, otp }, false),
  twofaSetupStart: (ticket: string) => post("auth.php", { action: "2fa_setup_start", ticket }, false),
  twofaSetupFinish: (ticket: string, code: string) => post("auth.php", { action: "2fa_setup_finish", ticket, code }, false),
  changePasswordTicket: (ticket: string, newPassword: string) => post("auth.php", { action: "change_password_ticket", ticket, newPassword }, false),
  forgotUrl: `${API_BASE}/forgot-password.php`,
};

/* --------------------------- برچسب‌ها --------------------------- */
export const pageLabels: Record<string, string> = {
  dashboard: "صفحه اصلی",
  finance: "مدیریت مالی",
  hr: "منابع انسانی",
  customers: "مدیریت مشتریان",
  vendors: "مدیریت فروشندگان",
  warehouse: "مدیریت گودام",
  production: "مدیریت تولید",
  reports: "گزارشات",
  chat: "گفتگو",
  backup: "بکاپ",
};
export const permLabels: Record<PermKey, string> = { view: "مشاهده", add: "افزودن", edit: "ویرایش", delete: "حذف", approve: "تایید" };
export const opLabels: Record<string, string> = {
  print: "چاپ",
  export: "خروجی اکسل / PDF",
  view_amounts: "مشاهده مبالغ",
  change_price: "تغییر قیمت",
  backdate: "ثبت با تاریخ گذشته",
  bulk_actions: "عملیات گروهی",
};
export const emptyPerm = (): Perm => ({ view: false, add: false, edit: false, delete: false, approve: false });
export const defaultPermissions = (): PermMap => {
  const m: PermMap = {};
  Object.keys(pageLabels).forEach((p) => (m[p] = emptyPerm()));
  m.dashboard.view = true;
  m.chat.view = true;
  m.chat.add = true;
  return m;
};
export const defaultOps = (): Record<string, boolean> => {
  const o: Record<string, boolean> = {};
  Object.keys(opLabels).forEach((k) => (o[k] = true));
  return o;
};
export const roleLabel = (r: string) => (r === "admin" ? "ادمین" : r === "manager" ? "مدیر" : "کاربر");
export const stateLabel: Record<string, string> = {
  active: "فعال",
  inactive: "غیرفعال",
  suspended: "معلق",
  invited: "دعوت‌شده",
  invite_expired: "دعوت منقضی",
  deleted: "بایگانی‌شده",
};
export const eventLabels: Record<string, string> = {
  usr_invite: "دعوت کاربر",
  usr_invite_resend: "ارسال مجدد دعوت",
  usr_invite_revoke: "لغو دعوت",
  usr_invite_accept: "پذیرش دعوت",
  usr_update: "ویرایش کاربر",
  usr_role_change: "تغییر نقش",
  usr_perm_change: "تغییر دسترسی",
  usr_state: "تغییر وضعیت",
  usr_delete: "حذف (بایگانی)",
  usr_restore: "بازیابی کاربر",
  usr_transfer: "انتقال مالکیت",
  usr_unlock: "باز کردن قفل",
  usr_locked: "قفل شدن حساب",
  usr_grant_add: "دسترسی موقت",
  usr_grant_revoke: "لغو دسترسی موقت",
  usr_template: "قالب نقش",
  usr_session_revoke: "بستن نشست",
  usr_profile: "ویرایش پروفایل",
  usr_password_change: "تغییر پسورد",
  usr_password_reset: "بازیابی پسورد",
  usr_reset_sent: "ارسال لینک بازیابی",
  usr_reset_request: "درخواست بازیابی",
  usr_force_pw: "الزام تغییر پسورد",
  usr_2fa_enable: "فعال‌سازی 2FA",
  usr_2fa_disable: "غیرفعال‌سازی 2FA",
  usr_2fa_reset: "ریست 2FA",
  usr_2fa_backup: "کدهای پشتیبان",
  usr_settings: "تغییر تنظیمات",
  usr_access_review: "بازبینی دسترسی",
  usr_login_blocked: "ورود مسدود",
  usr_approval_create: "درخواست تایید",
  usr_approval_approve: "تایید شد",
  usr_approval_reject: "رد شد",
  usr_approval_cancel: "لغو درخواست",
};

/* ------------------------- تاریخ شمسی ------------------------- */
let serverOffset = 0; // اختلاف ساعت دیتابیس با UTC (ثانیه)
export const setServerOffset = (n: number) => { serverOffset = n || 0; };

function parseDb(s?: string | null): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/.exec(s);
  if (!m) {
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }
  const ms = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0)) - serverOffset * 1000;
  return new Date(ms);
}
function fmtParts(d: Date, withTime: boolean): string {
  try {
    const f = new Intl.DateTimeFormat("fa-AF-u-ca-persian-nu-latn", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kabul" });
    const p: Record<string, string> = {};
    f.formatToParts(d).forEach((x) => { p[x.type] = x.value; });
    const date = `${p.year}/${p.month}/${p.day}`;
    return withTime ? `${date}  ${p.hour === "24" ? "00" : p.hour}:${p.minute}` : date;
  } catch {
    return d.toISOString().slice(0, withTime ? 16 : 10).replace("T", " ");
  }
}
export const fmtDT = (s?: string | null) => { const d = parseDb(s); return d ? fmtParts(d, true) : "—"; };
export const fmtD = (s?: string | null) => { const d = parseDb(s); return d ? fmtParts(d, false) : "—"; };
export const gregorian = (s?: string | null) => { const d = parseDb(s); return d ? d.toISOString().slice(0, 16).replace("T", " ") + " UTC" : ""; };
export function relTime(s?: string | null): string {
  const d = parseDb(s);
  if (!d) return "هرگز";
  const diff = Math.round((Date.now() - d.getTime()) / 1000);
  if (diff < 0) {
    const a = -diff;
    if (a < 3600) return `${Math.ceil(a / 60)} دقیقه دیگر`;
    if (a < 86400) return `${Math.floor(a / 3600)} ساعت دیگر`;
    return `${Math.floor(a / 86400)} روز دیگر`;
  }
  if (diff < 60) return "همین الان";
  if (diff < 3600) return `${Math.floor(diff / 60)} دقیقه پیش`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ساعت پیش`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)} روز پیش`;
  if (diff < 86400 * 365) return `${Math.floor(diff / (86400 * 30))} ماه پیش`;
  return `${Math.floor(diff / (86400 * 365))} سال پیش`;
}
/** مقدار فیلد datetime-local مرورگر (به وقت کابل) */
export const toLocalInput = (d: Date) => {
  const p = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kabul", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(d);
  const g: Record<string, string> = {};
  p.forEach((x) => { g[x.type] = x.value; });
  return `${g.year}-${g.month}-${g.day}T${g.hour === "24" ? "00" : g.hour}:${g.minute}`;
};

/* --------------------------- خروجی --------------------------- */
export function downloadCSV(filename: string, headers: string[], rows: (string | number | null | undefined)[][]) {
  const esc = (v: any) => {
    let s = v === null || v === undefined ? "" : String(v);
    if (/^[=+\-@]/.test(s)) s = "'" + s; // جلوگیری از اجرای فرمول در اکسل
    return `"${s.replace(/"/g, '""')}"`;
  };
  const csv = "﻿" + [headers, ...rows].map((r) => r.map(esc).join(",")).join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
export function copyText(t: string) {
  try { navigator.clipboard.writeText(t); } catch { /* ignore */ }
}
export const passwordScore = (v: string, min: number) => {
  let n = 0;
  if (v.length >= min) n++;
  if (/[a-z]/.test(v)) n++;
  if (/[A-Z]/.test(v)) n++;
  if (/[0-9]/.test(v)) n++;
  if (/[^A-Za-z0-9]/.test(v)) n++;
  return n;
};
